import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { airportService } from '../../../services/firestore';
import { parseExcelDate } from '../../../utils/contractDateUtils';
import { contactsService } from '../../../services/contactsService';
import { periodKind, UNKNOWN_STATUS } from '../../../services/contractRules';
import { USE_CONTRACTS_V2, v2SearchRecords, toLegacyAirport } from '../../../services/contractsV2';

// Filter Modes
export const FILTER_MODES = {
    OPEN: 'open',
    CLOSED: 'closed',
    ALL: 'all'
};

const isT1 = (str) => {
    const s = (str || '').toUpperCase();
    return s.includes('T1') || s.includes('TERMINAL 1') || s.includes('TERM 1') || s.includes('DXB 1');
};
const isT2 = (str) => {
    const s = (str || '').toUpperCase();
    return s.includes('T2') || s.includes('TERMINAL 2') || s.includes('TERM 2') || s.includes('DXB 2');
};

/** 'Monthly' | 'Daily' | null - the app's one rule: monthly from 30 days (contractRules, DUP-01). */
export const durationOf = (row) => periodKind(parseExcelDate(row.pickupDate), parseExcelDate(row.dropoffDate));

/** An open contract whose drop-off day has passed (isExpired) or is today (isToday). */
export const expiryStatusOf = (row, today) => {
    if (row.status !== 'Open') return { isExpired: false, isToday: false };
    const dropoff = parseExcelDate(row.dropoffDate);
    if (!dropoff) return { isExpired: false, isToday: false };

    const d = new Date(dropoff);
    d.setHours(0, 0, 0, 0);
    const t = new Date(today);
    t.setHours(0, 0, 0, 0);

    return {
        isExpired: d.getTime() < t.getTime(),
        isToday: d.getTime() === t.getTime()
    };
};

const applyType = (data, val) => val === 'All' ? data : data.filter(r => (r.customerType || '').toLowerCase() === val.toLowerCase());
const applyDuration = (data, val) => val === 'All' ? data : data.filter(r => durationOf(r) === val);
const applyBranch = (data, val) => {
    if (val === 'All') return data;
    if (val === 'T1') return data.filter(r => isT1(r.pickupBranch));
    if (val === 'T2') return data.filter(r => isT2(r.pickupBranch));
    return data;
};
const applyExpiry = (data, val, today) => {
    if (val === 'All') return data;
    return data.filter(r => {
        const { isExpired, isToday } = expiryStatusOf(r, today);
        return val === 'Expired' ? isExpired : isToday;
    });
};

/**
 * The counts on the filter chips. Each group is counted over the rows the OTHER active filters leave, and
 * its 'All' is that same pool - so 'All' = the rows that chip lists (with Expiry = Expired, the customer-type
 * 'All' is Corporate + Individual + rows with no type, not the whole search). Expiry 'All' is every row of
 * its pool, not expired + today (CON-21).
 */
export const chipCounts = (rows, { customerTypeFilter, durationFilter, branchFilter, expiryFilter }, today = new Date()) => {
    const dataForType = applyExpiry(applyBranch(applyDuration(rows, durationFilter), branchFilter), expiryFilter, today);
    const dataForDuration = applyExpiry(applyBranch(applyType(rows, customerTypeFilter), branchFilter), expiryFilter, today);
    const dataForBranch = applyExpiry(applyDuration(applyType(rows, customerTypeFilter), durationFilter), expiryFilter, today);
    const dataForExpiry = applyBranch(applyDuration(applyType(rows, customerTypeFilter), durationFilter), branchFilter);
    return {
        typeAll: dataForType.length,
        corporate: applyType(dataForType, 'Corporate').length,
        individual: applyType(dataForType, 'Individual').length,
        durationAll: dataForDuration.length,
        daily: applyDuration(dataForDuration, 'Daily').length,
        monthly: applyDuration(dataForDuration, 'Monthly').length,
        branchAll: dataForBranch.length,
        t1: applyBranch(dataForBranch, 'T1').length,
        t2: applyBranch(dataForBranch, 'T2').length,
        expiryAll: dataForExpiry.length,
        expiredCount: applyExpiry(dataForExpiry, 'Expired', today).length,
        todayCount: applyExpiry(dataForExpiry, 'Today', today).length,
    };
};

const normalizeForSearch = (str) => {
    if (!str && str !== 0) return "";
    let s = String(str).toLowerCase();
    return s.replace(/[^a-z0-9]/g, '');
};

// Phone search that survives the way numbers are written: "+966500005502",
// "00971 50 123 4567", "0501234567" and "501234567" all mean the same line. Both sides
// are reduced to their significant digits (country code and leading zeros dropped) and
// the row matches when its digits END with what was typed (6+ digits typed).
const fullDigits = (v) => String(v || '').replace(/\D/g, '').replace(/^00/, '');
const sigDigits = (v) => fullDigits(v).replace(/^(971|966)/, '').replace(/^0+/, '');
const phoneMatches = (typed, row) => {
    const t = sigDigits(typed);
    if (t.length < 4) return false;
    const contact = contactsService.peek(row.contractNo) || {};
    // A PART of the number is enough (4+ digits, anywhere in it). Typed WITH a country
    // code (+966…, 00971…, 966…): the code must match too, so a UAE and a Saudi line with
    // the same local digits are told apart. Typed without one (05…, 5…, 9225…): either
    // country matches and both lines are listed.
    const typedFull = fullDigits(typed);
    const withCode = /^(971|966)\d{4,9}$/.test(typedFull);
    return [row.phone, contact.p].some((p) => {
        if (withCode) return fullDigits(p).startsWith(typedFull) || fullDigits(p).includes(typedFull);
        const d = sigDigits(p); return d && d.includes(t);
    });
};
// The Customer ID column comes from the contacts store, so it is searchable too.
const identityMatches = (term, row) => {
    const contact = contactsService.peek(row.contractNo) || {};
    return !!contact.i && normalizeForSearch(contact.i).includes(term);
};
const rowMatches = (searchTerm, row) => {
    const term = normalizeForSearch(searchTerm);
    return normalizeForSearch(row.contractNo).includes(term) ||
           normalizeForSearch(row.plateNo).includes(term) ||
           normalizeForSearch(row.customer).includes(term) ||
           normalizeForSearch(row.bookingNumber).includes(term) ||
           normalizeForSearch(row.phone).includes(term) ||
           phoneMatches(searchTerm, row) ||
           identityMatches(term, row);
};

// The Salik + Darb and Parking cells (owner, 1 Oct 2026: "عمود سالك ودرب وباركينج ( سالك ودرب مع بعض )"): one part of
// the engine's answer (/api/salik/contracts-trips - Branch charges' rule) -> what the cell says. undefined = not
// answered yet ("…"), null = the engine does not know the contract ("—", never a made-up 0). The badge is the count;
// its colour says something is still to charge (warning), Unlimited Salik covers it (neutral) or all is on Ejar.
const AED = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
export const tripCellOf = (part, { unlimited = false, unit = ['trip', 'trips'] } = {}) => {
    if (part === undefined) return { kind: 'loading' };
    if (part === null) return { kind: 'unknown', title: 'Not in the engine yet - it shows after the next Ejar sync' };
    const n = Number(part.n) || 0;
    if (!n) return { kind: 'zero' };
    const charge = Number(part.charge) || 0;
    const owed = Number(part.owed) || 0;
    const what = `${n} ${n === 1 ? unit[0] : unit[1]}`;
    if (owed > 0.005) {
        return { kind: 'count', n, variant: 'warning', sub: AED.format(charge),
            title: `${what} · AED ${AED.format(charge)} charged · AED ${AED.format(owed)} not on Ejar yet` };
    }
    if (unlimited && (Number(part.unlimitedN) || 0) > 0) {
        return { kind: 'count', n, variant: 'neutral', sub: 'Unlimited',
            title: `${what} · covered by Unlimited Salik${charge > 0.005 ? ` · AED ${AED.format(charge)} on Ejar` : ''}` };
    }
    const other = (Number(part.mismatchN) || 0) > 0 ? ' · Ejar holds another amount - open the customer profile' : '';
    return { kind: 'count', n, variant: 'success', sub: AED.format(charge), title: `${what} · AED ${AED.format(charge)} · all on Ejar${other}` };
};

// The cards above the Airport table (owner, 1 Oct 2026: "كروت ... الداتا لايف لغاية امتى ايه اللى مش متسجل خاصة الفاينز
// والباركينج على العقود المفتوحه فقط ... وطبعا الكروت تتغير بناءا على الفرع"): what is not on Ejar yet on the OPEN contracts
// of the branch chosen, added up from the engine's figures per contract (the columns' own answer). While a contract has
// no answer yet (or a part is missing) that total is unknown (null) - a smaller sum is never shown as the whole.
const KINDS = ['fines', 'parking', 'gates'];
export const chargeCardsOf = (openRows, trips) => {
    const by = Object.fromEntries(KINDS.map((k) => [k, { owed: 0, items: 0, contracts: 0, missing: 0 }]));
    let contracts = 0, waiting = 0, unknown = 0, owing = 0, pending = 0, oldest = null;
    const seen = new Set();
    for (const r of openRows || []) {
        const cn = String(r.contractNo || '').trim().toUpperCase();
        if (!cn || seen.has(cn)) continue;
        seen.add(cn);
        contracts += 1;
        const t = trips[cn];
        if (t === undefined) { waiting += 1; continue; }
        if (t === null) { unknown += 1; continue; }
        let owes = false, lines = false;
        for (const k of KINDS) {
            const p = t[k];
            if (!p) { by[k].missing += 1; continue; }
            if ((Number(p.n) || 0) > 0) lines = true;
            const owed = Number(p.owed) || 0;
            if (owed > 0.005) {
                by[k].owed += owed;
                by[k].contracts += 1;
                // a fine Ejar holds at another amount owes its shortfall: it is one of the fines to fix
                by[k].items += (Number(p.notOnEjarN) || 0) + (k === 'fines' ? Number(p.mismatchN) || 0 : 0);
                owes = true;
            }
        }
        if (owes) owing += 1;
        if (lines) {
            if (!t.ejarCheckedAt) pending += 1;
            else if (!oldest || t.ejarCheckedAt < oldest) oldest = t.ejarCheckedAt;
        }
    }
    const known = (k) => (waiting === 0 && by[k].missing === 0 ? Math.round(by[k].owed * 100) / 100 : null);
    const parts = Object.fromEntries(KINDS.map((k) => [k, { owed: known(k), items: by[k].items, contracts: by[k].contracts }]));
    const all = KINDS.every((k) => parts[k].owed !== null) ? Math.round(KINDS.reduce((a, k) => a + parts[k].owed, 0) * 100) / 100 : null;
    return { contracts, waiting, unknown, owing, total: all, ...parts, ejar: { oldest, pending } };
};

// "13:40" today, "30/09 13:40" another day, "—" never (an engine time "YYYY-MM-DD HH:MM:SS", Dubai).
export const liveAt = (iso, today = new Date()) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/.exec(String(iso || ''));
    if (!m) return '—';
    const sameDay = +m[1] === today.getFullYear() && +m[2] === today.getMonth() + 1 && +m[3] === today.getDate();
    return sameDay ? `${m[4]}:${m[5]}` : `${m[3]}/${m[2]} ${m[4]}:${m[5]}`;
};

export function useAirportLogic() {
    const [openContracts, setOpenContracts] = useState([]);
    const [closedContracts, setClosedContracts] = useState([]);
    const [hasLoadedClosed, setHasLoadedClosed] = useState(false);
    
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterMode, setFilterMode] = useState(FILTER_MODES.OPEN);
    const [customerTypeFilter, setCustomerTypeFilter] = useState('All'); // 'All', 'Corporate', 'Individual'
    const [durationFilter, setDurationFilter] = useState('All'); // 'All', 'Daily', 'Monthly'
    const [branchFilter, setBranchFilter] = useState('All'); // 'All', 'T1', 'T2'
    const [expiryFilter, setExpiryFilter] = useState('All'); // 'All', 'Expired', 'Today'
    // Pre-fill the search from ?search= (the AI Assistant routes airport customers here by phone / contract).
    const [searchParams] = useSearchParams();
    const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
    const [remoteResults, setRemoteResults] = useState([]);   // server hits for a contract not in the loaded set
    useEffect(() => {
        const q = searchParams.get('search');
        if (q) { setSearchTerm(q); setFilterMode(FILTER_MODES.ALL); }
    }, [searchParams]);

    // A closed (or old) contract is not in the loaded open list. When there is a search term,
    // look it up everywhere: by contract / booking number on the server, and by phone, Customer ID
    // or name in the contacts store (every contract of that customer, open or closed, any date),
    // whose airport contracts are then read by id - so the search finds them whatever tab is on.
    useEffect(() => {
        const term = searchTerm.trim();
        if (term.length < 4) { setRemoteResults([]); return undefined; }
        let alive = true;
        const t = setTimeout(async () => {
            try {
                if (USE_CONTRACTS_V2) {
                    // the contracts store searches every airport contract (any date, open or closed): number, booking,
                    // plate, name - also slightly misspelt -, phone with or without the code, Customer ID
                    const found = (await v2SearchRecords(term, 'airport')).map(toLegacyAirport);
                    if (alive) setRemoteResults(normalizeData(found));
                    return;
                }
                const direct = await airportService.getAirportContractsByIdentifier(term).catch(() => []);
                await contactsService.ensureLoaded().catch(() => {});
                const ids = contactsService.findCustomers(term, { limit: 5, fuzzy: false })
                    .flatMap((g) => g.rows.map((r) => r.c)).slice(0, 60);
                const more = ids.length ? await airportService.getAirportContractsByIds(ids).catch(() => []) : [];
                const byId = new Map([...(direct || []), ...more].map((r) => [r.id, r]));
                if (alive) setRemoteResults(normalizeData([...byId.values()]));
            } catch (e) {
                if (alive) setRemoteResults([]);
            }
        }, 350);
        return () => { alive = false; clearTimeout(t); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchTerm]);

    const normalizeData = (data) => {
        return data.map(d => ({
            id: d.id,
            contractNo: d['Contract No.'] || d.contract_no || d.id,
            bookingNumber: d['Booking Number'] || d.booking_number || '',
            customer: d['Customer'] || d.customer || '',
            pickupDate: d['Pick-up Date'] || d.pickup_date || '',
            dropoffDate: d['Drop-off Date'] || d.dropoff_date || '',
            plateNo: d['Plate No.'] || d.plate_no || '',
            model: d['Model'] || d.model || '',
            status: d['Status'] || d.status || UNKNOWN_STATUS,   // blank is unknown, never 'Open'
            phone: d['Phone Number'] || d.phone || '',
            rentalRate: d['Rental Rate'] || d.rental_rate || '',
            lastUpdated: d.last_updated,
            pickupBranch: d['Pick-up Branch'] || d.pickup_branch || '',
            customerType: d['Customer Type'] || d.customer_type || '',
            contractType: d['Contract Type'] || d.contract_type || '',
            createdBy: d['Created By'] || d.created_by || d['Opened By'] || d['Opened by'] || d['Staff'] || d['User'] || ''
        }));
    };

    // Load Open Contracts (Initial). `silent`: refresh the rows in place, without replacing the
    // table by the full-page spinner (a refresh every 20 s used to blank the page each time - CON-22).
    const loadOpenData = useCallback(async ({ silent = false } = {}) => {
        try {
            if (!silent) setLoading(true);
            const result = await airportService.getOpenAirportContracts();
            setOpenContracts(normalizeData(result));
            setError(null);
        } catch (err) {
            if (!silent) setError(err);
        } finally {
            if (!silent) setLoading(false);
        }
    }, []);

    // Load Closed Contracts (Lazy): the newest ~500 by drop-off date, then "Load older" one page at a
    // time from where the last page stopped (CON-19: "Load All History" read every closed contract).
    const [isPartialClosed, setIsPartialClosed] = useState(true);
    // Where "Load older" starts: the new store's `cursor` (exactly after the last row loaded, also among the
    // contracts with no drop-off), kept HERE and given back as it came - it used to live only in contractsV2's
    // memory, keyed by the days number, and was lost whenever that module loaded again; and the days back the last
    // page reached (the Firestore copy has no cursor: it pages by days).
    const [closedFrom, setClosedFrom] = useState({ offsetDays: 0, cursor: '' });
    const [closedSince, setClosedSince] = useState('');         // oldest drop-off day loaded

    const loadClosedData = useCallback(async (older = false) => {
        if (hasLoadedClosed && !older) return;
        try {
            setLoading(true);
            const from = older ? closedFrom : { offsetDays: 0, cursor: '' };
            const page = await airportService.getClosedAirportPage({
                fromOffsetDays: from.offsetDays, cursor: from.cursor || undefined, limitCount: 500, maxSpanDays: older ? 366 : 120,
            });
            const rows = normalizeData(page.rows);
            setClosedContracts((prev) => {
                if (!older) return rows;
                const seen = new Set(prev.map((r) => r.id));
                return [...prev, ...rows.filter((r) => !seen.has(r.id))];
            });
            setClosedFrom({ offsetDays: page.nextOffsetDays, cursor: page.cursor || '' });
            const since = new Date(); since.setDate(since.getDate() - page.nextOffsetDays + 1);
            setClosedSince(`${String(since.getDate()).padStart(2, '0')}/${String(since.getMonth() + 1).padStart(2, '0')}/${since.getFullYear()}`);
            setHasLoadedClosed(true);
            setIsPartialClosed(!page.done);
        } catch (err) {
            setError(err);
        } finally {
            setLoading(false);
        }
    }, [hasLoadedClosed, closedFrom]);

    useEffect(() => {
        loadOpenData();
    }, [loadOpenData]);

    useEffect(() => {
        if (filterMode === FILTER_MODES.CLOSED) {
            loadClosedData(false); // Default to limited load
        }
    }, [filterMode, loadClosedData]);

    const preExpiryFilteredData = useMemo(() => {
        let source = [];
        if (filterMode === FILTER_MODES.OPEN) source = openContracts;
        else if (filterMode === FILTER_MODES.CLOSED) source = closedContracts;
        else source = [...openContracts, ...closedContracts];

        // Add server-fetched matches (a recently-closed contract not in the loaded set), deduped.
        if (searchTerm.trim() && remoteResults.length) {
            const have = new Set(source.map(r => r.id));
            source = [...source, ...remoteResults.filter(r => !have.has(r.id))];
        }

        // 1. Customer Type Filter
        if (customerTypeFilter !== 'All') {
            source = source.filter(row => {
                const type = (row.customerType || '').toLowerCase();
                return type === customerTypeFilter.toLowerCase();
            });
        }

        // 2. Duration Filter (no dates = in neither Daily nor Monthly)
        if (durationFilter !== 'All') {
            source = source.filter(row => durationOf(row) === durationFilter);
        }

        // 3. Branch Filter (T1, T2)
        if (branchFilter !== 'All') {
            source = source.filter(row => {
                if (branchFilter === 'T1') return isT1(row.pickupBranch);
                if (branchFilter === 'T2') return isT2(row.pickupBranch);
                return true;
            });
        }

        // 4. Search Filter
        if (searchTerm.trim()) {
            source = source.filter(row => rowMatches(searchTerm, row));
        }

        return source;
    }, [openContracts, closedContracts, remoteResults, filterMode, customerTypeFilter, durationFilter, branchFilter, searchTerm]);

    const getExpiryStatus = expiryStatusOf;

    const filteredData = useMemo(() => {
        let source = preExpiryFilteredData;

        // Apply Expiry Filter
        if (expiryFilter !== 'All') {
            const today = new Date();
            source = source.filter(row => {
                const { isExpired, isToday } = getExpiryStatus(row, today);
                if (expiryFilter === 'Expired') return isExpired;
                if (expiryFilter === 'Today') return isToday;
                return true;
            });
        }

        // Sort by pick-up date, newest first (today at the top). The cell is
        // "dd/mm/yyyy" optionally followed by " HH:MM"; the old parser split on
        // "/" and read the year as "2026 17:44" -> NaN, so nothing actually sorted.
        const parseDate = (d) => {
            if (!d) return 0;
            const s = String(d).trim();
            const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(\d{1,2}):(\d{2}))?/.exec(s);
            if (m) return new Date(+m[3], +m[2] - 1, +m[1], +(m[4] || 0), +(m[5] || 0)).getTime();
            const t = new Date(s).getTime();
            return isNaN(t) ? 0 : t;
        };
        // Closed: the most recently RETURNED first (drop-off), not the most recently picked up.
        const key = filterMode === FILTER_MODES.CLOSED ? 'dropoffDate' : 'pickupDate';
        return [...source].sort((a, b) => parseDate(b[key]) - parseDate(a[key]));

    }, [preExpiryFilteredData, expiryFilter, getExpiryStatus, filterMode]);

    const stats = useMemo(() => {
        const fullBase = filterMode === FILTER_MODES.OPEN ? openContracts : 
                        (filterMode === FILTER_MODES.CLOSED ? closedContracts : [...openContracts, ...closedContracts]);

        const searchFiltered = searchTerm.trim() ? fullBase.filter(row => rowMatches(searchTerm, row)) : fullBase;

        const statsResult = {
            open: openContracts.length,
            closed: hasLoadedClosed ? closedContracts.length : '-',
            total: openContracts.length + closedContracts.length,
            ...chipCounts(searchFiltered, { customerTypeFilter, durationFilter, branchFilter, expiryFilter }, new Date()),
            all: searchFiltered.length // Total pool for current Status/Search
        };

        return statsResult;
    }, [openContracts, closedContracts, hasLoadedClosed, filterMode, customerTypeFilter, durationFilter, branchFilter, expiryFilter, searchTerm]);

    // the open contracts of the branch chosen, whatever the other filters (the cards: "على العقود المفتوحه فقط")
    const openOfBranch = useMemo(() => applyBranch(openContracts, branchFilter), [openContracts, branchFilter]);

    return {
        loading,
        error,
        data: filteredData,
        openContracts,
        openOfBranch,
        stats,
        filterMode,
        setFilterMode,
        customerTypeFilter,
        setCustomerTypeFilter,
        durationFilter,
        setDurationFilter,
        branchFilter,
        setBranchFilter,
        expiryFilter,
        setExpiryFilter,
        searchTerm,
        setSearchTerm,
        isPartialClosed,
        closedSince,
        loadAllClosed: () => loadClosedData(true),
        refreshData: () => {
             setHasLoadedClosed(false); 
             loadOpenData();
             if (filterMode === FILTER_MODES.CLOSED) {
                 loadClosedData(false);
             }
        },
        // the open rows again, in place (no spinner, closed history kept) - after the phone fill
        refreshOpenQuietly: () => loadOpenData({ silent: true }),
        getExpiryStatus
    };
}
